import { useState } from 'react';
import { usePoses } from '../data/poses';
import { api } from '../api/client';
import type { RoutinesApi } from '../hooks/useRoutines';
import type { RoutineStep } from '../types';
import { RoutineBuilderPage } from './RoutineBuilderPage';

interface Props {
  routines: RoutinesApi;
}

const DURATIONS = [5, 10, 15, 20, 30, 45, 60];
const LEVELS = [
  { value: 1, label: 'Beginner' },
  { value: 2, label: 'Intermediate' },
  { value: 3, label: 'Advanced' },
];

/**
 * "Generate a routine" — collects a few criteria, asks the AI to compose an
 * ordered sequence from the *existing* catalog, then hands the draft straight
 * into the normal builder for review and fine-tuning. Nothing is saved until
 * the user hits Save in the builder.
 */
export function GenerateRoutinePage({ routines }: Props) {
  const poses = usePoses();
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [duration, setDuration] = useState(15);
  const [maxLevel, setMaxLevel] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; steps: RoutineStep[] } | null>(null);

  // Once generated, drop into the builder pre-filled — same edit surface as
  // every other routine, so there's no separate editing UI to maintain.
  if (draft) {
    return (
      <RoutineBuilderPage routines={routines} initialName={draft.name} initialSteps={draft.steps} />
    );
  }

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.generateRoutine({
        name: name.trim() || 'Generated routine',
        purpose: purpose.trim(),
        durationMinutes: duration,
        maxLevel,
        poses: poses.map((p) => ({
          id: p.id,
          name: p.nameEnglish,
          tier: p.difficultyTier,
          category: p.category,
          bilateral: !!p.bilateral,
        })),
      });
      if (!result.steps.length) {
        setError(
          'The generator could not build a sequence from your catalog. Try a higher max level or a broader purpose.',
        );
        return;
      }
      setDraft({ name: result.name, steps: result.steps });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Generate a routine</h1>
        <a className="back" href="#/routines">
          ← Routines
        </a>
      </div>

      <div className="gen-form">
        <label className="gen-field">
          <span className="gen-label">Name</span>
          <input
            type="text"
            value={name}
            placeholder="e.g. Morning Wake-Up"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="gen-field">
          <span className="gen-label">Purpose</span>
          <textarea
            rows={2}
            value={purpose}
            placeholder="e.g. Gentle hip openers to wind down before bed"
            onChange={(e) => setPurpose(e.target.value)}
          />
        </label>

        <div className="gen-field">
          <span className="gen-label">Duration</span>
          <div className="gen-chips">
            {DURATIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`chip${duration === d ? ' on' : ''}`}
                onClick={() => setDuration(d)}
              >
                {d} min
              </button>
            ))}
          </div>
        </div>

        <div className="gen-field">
          <span className="gen-label">Max level</span>
          <div className="gen-chips">
            {LEVELS.map((l) => (
              <button
                key={l.value}
                type="button"
                className={`chip${maxLevel === l.value ? ' on' : ''}`}
                onClick={() => setMaxLevel(l.value)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="gen-error">{error}</p>}

        <div className="builder-actions">
          <a className="btn" href="#/routines">
            Cancel
          </a>
          <button type="button" className="btn primary" disabled={busy} onClick={generate}>
            {busy ? 'Generating…' : '✨ Generate routine'}
          </button>
        </div>
        <p className="hint">
          The generator drafts a sequence from your pose catalog. You can reorder, retime, add, or
          remove poses before saving.
        </p>
      </div>
    </>
  );
}
